import { Button, Link, Typography } from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import { AssignmentOutlined } from "@material-ui/icons"
import { Desktop, Mobile } from "common/MediaQueries"
import pollutionColors from "components/carModelPage/sections/specifications/pollutionColors"
import safetyColors from "components/carModelPage/sections/specifications/safetyColors"
import { Font } from "components/mapPages/showRooms/ShowRoomItem"
import { selectedDetailingSelector } from "components/shopFeatures/detailing/detailingSelectors"
import { useLayoutContext } from "layout/oldLayout/LayoutContext"
import { useState } from "react"
import { useLocation } from "react-router-dom"
import { useTypedSelector } from "store"
import styled from "styled-components"
import { Theme } from "theme/theme"
import { STEPS_EN } from "../constants"
import ImageModal from "./ImageModal"

const StyledContainer = styled.div<{ $pathname: string }>`
  position: relative;
  margin: 0;
  padding: 0;
  z-index: 1;

  ${(p) => p?.$pathname === STEPS_EN.color && "padding: 0 32px;"}
`

const StyledPollutionAndSafetyContainer = styled.div`
  display: flex;
  gap: 13px;
`

const StyledButton = styled(Button)`
  border: 1px solid ${(p) => p.theme.colors.borderGrey};
  border-radius: 3px;
  padding: 10px;
  /* flex-shrink: 0; */
  &:focus-visible {
    border: 2px dashed white;
    outline: 3px dashed #0e3487;
    outline-offset: 2px;
  }

  > [class*="MuiButton-label"] {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`
const StyledButtonWheels = styled(Button).attrs({ target: "_blank" })<{
  $mobile?: boolean
  $isShowRoomsScreens?: boolean
}>`
  ${(p) => p.$mobile && `margin: 0 0 15px 0;`}
  width: 120px;
  /* flex-shrink: 0; */
  ${(p) =>
    p.$isShowRoomsScreens &&
    `border: 1px solid ${p.theme.colors.borderGrey};
    border-radius: 3px;
    padding: 10px;
    p{
      font-size:12px;
    }`}
`

const StyledWheelsLabel = styled(Typography)`
  font-family: ${Font.OENaPro};
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.05em;
  display: inline-block;
`

const StyledLabel = styled.span`
  font-family: ${Font.OENaPro};
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0em;
`

const StyledNumber = styled.span<{ backgroundColor: string; color: string }>`
  width: 18px;
  height: 18px;

  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;

  background-color: ${(p) => p.backgroundColor};
  color: ${(p) => p.color};

  font-family: ${Font.OENaPro};
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0em;
`

const StyledDisclaimer = styled.p<{ $invertColor: boolean }>`
  font-family: ${Font.OENaPro};
  font-size: 10px;
  font-weight: 300;
  line-height: 13px;
  letter-spacing: 0em;
  color: ${(p) => (p.$invertColor ? "white" : "black")};
`

const StyledAssignmentOutlinedIcon = styled(AssignmentOutlined)`
  margin-left: 5px;
  width: 13px;
`

const StyledLabelLink = styled(Typography)`
  font-family: ${Font.OENaPro};
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0em;
  margin-top: 6px;
`

const StyledLabelAndBtnContainer = styled.div`
  display: flex;
`

const PollutionAndSafety = ({ invertColor }: { invertColor: boolean }) => {
  const [openModal, setOpenModal] = useState<"pollution" | "safety" | null>(
    null
  )
  const { pathname } = useLocation()
  const { isShowRoomsScreens } = useLayoutContext()
  const {
    colors: { white, black },
  } = useTheme<Theme>()

  const themeColor = invertColor ? "secondary" : "primary"

  const selectedDetailing = useTypedSelector(selectedDetailingSelector)

  if (!selectedDetailing) return null

  const { pollutionLevel, safetyLevel, tireSpecImage, regulation } =
    selectedDetailing.details

  return (
    <StyledContainer $pathname={pathname}>
      <Mobile>
        <StyledButtonWheels
          $mobile={true}
          color={themeColor}
          href={tireSpecImage.imageUrl}
        >
          <StyledAssignmentOutlinedIcon />
          <StyledWheelsLabel>נתוני צמיגים</StyledWheelsLabel>
        </StyledButtonWheels>
      </Mobile>

      <StyledPollutionAndSafetyContainer>
        <StyledButton
          color={themeColor}
          onClick={() => setOpenModal("pollution")}
          aria-expanded={openModal === "pollution" ? true : false}
        >
          <StyledLabelAndBtnContainer>
            <StyledLabel>דרגת זיהום</StyledLabel>
            <StyledNumber
              backgroundColor={pollutionColors[pollutionLevel - 1]}
              color={
                pollutionLevel <= 4 || pollutionLevel >= 13 ? white : black
              }
            >
              {pollutionLevel}
            </StyledNumber>
          </StyledLabelAndBtnContainer>
          <StyledLabelLink color={themeColor}>לפרטים נוספים</StyledLabelLink>
        </StyledButton>

        <ImageModal
          open={openModal === "pollution"}
          label="דרגת זיהום"
          onClose={() => setOpenModal(null)}
          regulation={regulation}
          modalType={openModal}
        />

        <StyledButton
          color={themeColor}
          onClick={() => setOpenModal("safety")}
          aria-expanded={openModal === "safety" ? true : false}
        >
          <StyledLabelAndBtnContainer>
            <StyledLabel>רמת האבזור הבטיחותי</StyledLabel>
            <StyledNumber
              backgroundColor={safetyColors[safetyLevel]}
              color={safetyLevel <= 5 ? black : white}
            >
              {safetyLevel}
            </StyledNumber>
          </StyledLabelAndBtnContainer>
          <StyledLabelLink color={themeColor}>לפרטים נוספים</StyledLabelLink>
        </StyledButton>

        <ImageModal
          open={openModal === "safety"}
          label="רמת האבזור הבטיחותי"
          onClose={() => setOpenModal(null)}
          regulation={regulation}
          modalType={openModal}
        />

        <Desktop>
          <StyledButtonWheels
            $isShowRoomsScreens={isShowRoomsScreens}
            color={themeColor}
            href={tireSpecImage.imageUrl}
          >
            <StyledAssignmentOutlinedIcon />
            <StyledWheelsLabel color={themeColor}>
              נתוני צמיגים
            </StyledWheelsLabel>
          </StyledButtonWheels>
        </Desktop>
      </StyledPollutionAndSafetyContainer>

      <StyledDisclaimer $invertColor={invertColor}>
        התמונה להמחשה בלבד. בכפוף ל
        <Link
          href="/מאזדה_אתר_הזמנות_אוןליין_תקנון_2209_G3693-V001.pdf"
          target="_blank"
          color={themeColor}
        >
          תקנון
        </Link>
        . לכל פניה ושאלה ניתן לפנות למוקד שירות הלקוחות בטלפון{" "}
        <Link href="tel:08-9139995" color={themeColor}>
          08-9139995
        </Link>
        .
      </StyledDisclaimer>
    </StyledContainer>
  )
}

export default PollutionAndSafety
